import { Grid, Typography } from '@mui/material';
import { Field, Form, Formik, FormikProps, useFormikContext } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FormikEnumerationSelect, FormikToggleButton, FormikTextField } from 'src/common/components/formik';
import { WO2Modal } from 'src/common/components/Modal';
import { LoadingProgress } from 'src/common/store/types/loadingProgress';
import * as yup from 'yup';
import { CreateTemplateRequest, TemplateFeeTierRequest, TemplateThunkResponse } from '../../store';
import { FeeDisplayFrequencyEnum, FeeNatureEnum, FeeSubCategoryEnum, FeeValueMethodEnum } from '../../../store/enums';
import { TieredFeeFields } from './tieredFeeFields';
import { FlatRateFeeFields } from './flatRateFeeFields';
import { FlatDollarFeeFields } from './flatDollarFeeFields';
import { OneOffFeeFields } from './oneOffFeeFields';
import { ToggleButtonItem } from 'src/common';
import { AssetFields } from './assetFields';
import { Enumeration } from 'src/common/types';

export interface Props {
  model: CreateTemplateRequest;
  onClose: () => void;
  onConfirm: (payload: CreateTemplateRequest) => Promise<TemplateThunkResponse>;
  confirmProgress: LoadingProgress;
}

class FeeFormStructureEnum extends Enumeration {
  static FlatRate = new FeeFormStructureEnum(1, 'flatRate', 'Flat rate');
  static FlatDollar = new FeeFormStructureEnum(2, 'flatDollar', 'Flat dollar');
  static Tiered = new FeeFormStructureEnum(3, 'tiered', 'Tiered');
  static OneOff = new FeeFormStructureEnum(4, 'oneOff', 'One-off');

  static getById = (status: number): FeeFormStructureEnum | undefined => {
    return Object.values(FeeFormStructureEnum)
      .filter((t) => t instanceof FeeFormStructureEnum)
      .find((t: FeeFormStructureEnum) => t.id === status);
  };
}

export function TemplateDialog({ model, onClose, onConfirm, confirmProgress }: Props): JSX.Element {
  interface TemplateForm extends CreateTemplateRequest {
    feeStructureId: number;
  }

  const formRef = useRef<FormikProps<TemplateForm>>(null);
  const loaded = useRef<string[]>([]);
  const [feeComponent, setFeeComponent] = useState<JSX.Element | null>(null);
  const [showAssets, setShowAssets] = useState<boolean>(false);

  const AutoFormComplete = useCallback(() => {
    const { values, setFieldValue } = useFormikContext<TemplateForm>();

    const generateFeeName = () => {
      if (values.subCategoryId === null || values.displayFrequencyId === null) return;
      
      const subCategoryName = FeeSubCategoryEnum.getById(values.subCategoryId)?.displayName;
      const structure = (() => {
        switch (values.feeStructureId) {
          case FeeFormStructureEnum.FlatDollar.id: return `Flat $ (${FeeDisplayFrequencyEnum.getById(values.displayFrequencyId)?.name})`;
          case FeeFormStructureEnum.FlatRate.id: return 'Flat %';
          case FeeFormStructureEnum.OneOff.id: return 'One-off';
          case FeeFormStructureEnum.Tiered.id: return 'Tiered';
        }
      })();

      return `${subCategoryName} — ${structure}`;
    };

    // On fee form structure change
    useEffect(() => {
      if (!loaded.current.includes('feeStructure')) {
        loaded.current.push('feeStructure');
        return;
      }

      // Reset tiers for flat structures
      if (values.feeStructureId === FeeFormStructureEnum.FlatRate.id) {
        setFieldValue('feeTiers', [{ from: 0, to: 0, valueWithGst: 0, valueMethod: FeeValueMethodEnum.Percent.name } as TemplateFeeTierRequest]);
      }
      else if (values.feeStructureId === FeeFormStructureEnum.FlatDollar.id) {
        setFieldValue('feeTiers', [{ from: 0, to: 0, valueWithGst: 0, valueMethod: FeeValueMethodEnum.Dollar.name } as TemplateFeeTierRequest]);
      }
      
      // Reset display frequency
      setFieldValue('displayFrequencyId', 
        values.feeStructureId === FeeFormStructureEnum.OneOff.id ? 
          FeeDisplayFrequencyEnum.OneOff.id : 
          FeeDisplayFrequencyEnum.Annual.id
      );

      // Reset fee nature
      setFieldValue('feeNatureId', 
        values.feeStructureId === FeeFormStructureEnum.OneOff.id ? 
          FeeNatureEnum.OneOff.id : 
          FeeNatureEnum.Ongoing.id
      );
    }, [
      values.feeStructureId,
      setFieldValue,
    ]);

    useEffect(() => {
      // reset show assets
      setShowAssets(values.feeStructureId != FeeFormStructureEnum.FlatDollar.id);

      // Update fee component
      switch (values.feeStructureId) {
        case FeeFormStructureEnum.FlatRate.id: setFeeComponent(<FlatRateFeeFields />); break;
        case FeeFormStructureEnum.FlatDollar.id: setFeeComponent(<FlatDollarFeeFields />); break;
        case FeeFormStructureEnum.Tiered.id: setFeeComponent(<TieredFeeFields />); break;
        case FeeFormStructureEnum.OneOff.id: setFeeComponent(<OneOffFeeFields />); break;
      }
    }, [
      values.feeStructureId,
      setFeeComponent,
      setShowAssets,
    ])

    // On category, structure or display frequency change
    useEffect(() => {
      if (!loaded.current.includes('feeName')) {
        loaded.current.push('feeName');
        return;
      }

      const generatedName = generateFeeName();
      // Update name if different
      if (values.feeName !== generatedName) {
        setFieldValue("feeName", generatedName, false);
      }
    }, [
      values.feeStructureId,
      values.subCategoryId,
      values.displayFrequencyId,
      setFieldValue,
    ]);

    return null;
  }, [setFeeComponent, setShowAssets]);

  const feeStructureToggleButtons: ToggleButtonItem<number>[] = 
    Object.values(FeeFormStructureEnum)
      .filter((t) => t instanceof FeeFormStructureEnum)
      .map((feeStructure: FeeFormStructureEnum) => {
        return { name: feeStructure.displayName, value: feeStructure.id };
      }
    );

  const initialFormValues: CreateTemplateRequest & { feeStructureId: number } = { 
    ...model,
    feeStructureId: (() => {
      if (model.feeNatureId == FeeNatureEnum.OneOff.id)
        return FeeFormStructureEnum.OneOff.id;
      if (model.feeTiers.length == 1){
        const feeTier = model.feeTiers[0];

        if (feeTier.from === 0 && feeTier.to === 0)
          return feeTier.valueMethod == FeeValueMethodEnum.Dollar.name ?
            FeeFormStructureEnum.FlatDollar.id :
            FeeFormStructureEnum.FlatRate.id;
      }

      return FeeFormStructureEnum.Tiered.id;
    })()
   };

  return (
    <WO2Modal
      MuiDialogProps={{
        open: true,
        onClose: () => {
          onClose();
        },
        maxWidth: 'md',
      }}
      title={<>Edit Fee Template</>}
      actions={[
        {
          label: 'Cancel',
          onClickHandler: () => {
            onClose();
          },
          type: 'outlined',
          disabled: false,
        },
        {
          label: 'Save',
          onClickHandler: () => {
            formRef.current?.submitForm();
          },
          type: 'contained',
          disabled: confirmProgress.isLoading,
          buttonType: 'submit',
        },
      ]}
    >
      <>
        <Formik<TemplateForm>
          innerRef={formRef}
          enableReinitialize={true}
          initialValues={initialFormValues}
          onSubmit={(values: TemplateForm) => {
            onConfirm(values).then((response) => {
              if (response.variant === undefined) {
                onClose();
              }
            });
          }}
          validationSchema={yup.object({
            subCategoryId: yup.number().required().typeError("Fee Type is required")
          })}
        >
          {() => (
            <Form>
              <AutoFormComplete />
              <fieldset style={{ border: 'none', padding: '0', paddingTop: '20px' }}>
                <Grid container justifyContent="flex-start" spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant={'h5'} style={{ paddingBottom: '10px', alignSelf: 'flex-start' }}>Fee Type</Typography>
                    <Field
                      component={FormikEnumerationSelect}
                      type={FeeSubCategoryEnum}
                      showNone={false}
                      name="subCategoryId"
                      label=""
                      fullWidth
                      valueIsId={true}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant={'h5'} style={{ paddingBottom: '10px', alignSelf: 'flex-start' }}>Fee Structure</Typography>
                    <Field
                      component={FormikToggleButton}
                      exclusive={true}
                      showRequiredAsterisk={false}
                      buttons={feeStructureToggleButtons}
                      name="feeStructureId"
                      label=""
                    />
                  </Grid>
                  { feeComponent }
                  <Grid item xs={12}>
                    <Typography variant={'h5'} style={{ paddingBottom: '10px', alignSelf: 'flex-start' }}>Template Name</Typography>
                    <Field
                      component={FormikTextField}
                      disabled={false}
                      name="feeName"
                      label=""
                      fullWidth
                    />
                  </Grid>
                  { showAssets && <AssetFields /> }
                </Grid>
              </fieldset>
            </Form>
          )}
        </Formik>
      </>
    </WO2Modal>
  );
}
